/**
 * 按键去掉地址里的参数：别的参数、路径、hash 逐字节不动（不经 URLSearchParams 重新编码 —— 那会把 `%20` 改成 `+`、`/` 改成 `%2F`、
 * 键的顺序与写法也可能变）；问号只在一个参数都不剩时才去掉。真去掉了键时，空段（`&&`、开头 / 结尾的 `&`）一起去掉，
 * 不留 `?&x=1`、`?&`（B25 复核 Codex P3）；一个键都没去掉 ⇒ 原样还回去（空段也不动）。
 * 键按 vue-router 3.6.5 `parseQuery` 的读法认（`+` 当空格、再解码）。
 *
 * 为什么不用字符串替换（多版本菜单 N35-B2 第 4 轮 + B5 review R4B5-1）：购物车原来 `href.replace('?verifyCouponCode=true', '')`，
 * 连问号一起删了；进店参数与点单链接码随地址带以后，这个参数后面总跟着 `&menuLink=…&mmEntry=…`，删完变成
 * `/cart/t/7&menuLink=…&mmEntry=table` —— 刷新 / 后退回这一条时它们被当成桌号路径的一部分（桌号 = 整串，写进订单）。
 *
 * 另有 `parseRouteQuery`：路由器读 query 的办法（`src/router/index.js` 的 `parseQuery` 选项，N35-B2 第 5 轮复核）。
 *
 * 本文件是纯函数，不 import 任何别的模块。
 */

/** 解码一次；解不开（裸 `%`、坏的多字节）就原样还回去 —— 与 vue-router 3.6.5 内部的 `decode` 相同（它另外在开发环境打一条警告） */
function decodeOrKeep(str) {
  try {
    return decodeURIComponent(str)
  } catch (e) {
    return str
  }
}

/** query string 里一段 `key=value` 的键（解码；解不了就原样） */
function queryPartKey(part) {
  return decodeOrKeep(part.split('=')[0].replace(/\+/g, ' '))
}

/**
 * 路由器读 query 的办法（`new VueRouter({ parseQuery })`；多版本菜单 N35-B2 第 5 轮复核，Codex P1）：与 vue-router 3.6.5 自带的 `parseQuery`
 * 逐条相同 —— 去掉开头的 `?` / `#` / `&`，按 `&` 切，`+` 当空格再解码，没有 `=` 的键给 null，重复键给数组。
 * 为什么要自己给：测试机与生产的构建装的是 **3.3.4**（不按锁文件装，见 tests/vendor/vue-router@3.3.4），它自带的那份解码不留退路，
 * 任何一个值解不开（`label=5%`、老守卫 `splicingQuery` 把解码后的自填桌号原样拼回来的 `tableNumber=5%`）就把**整串** query 丢掉 ——
 * 进店参数 mmEntry、点单链接码 menuLink 一起丢（落地成主菜单），老守卫与 N35 守卫还会为补参数来回打转不落地；3.6.5 只把那一个值原样留下。
 * 换成这一份之后两个版本读法一致，3.6.5 上结果与它自带的完全相同（只少那条开发环境警告）。不会抛。
 * @param {String} query `?` 后面那一段（可以带着 `?`）
 * @returns {Object}
 */
export function parseRouteQuery(query) {
  const res = {}
  const text = String(query || '').trim().replace(/^(\?|#|&)/, '')
  if (!text) return res
  text.split('&').forEach(param => {
    const parts = param.replace(/\+/g, ' ').split('=')
    const key = decodeOrKeep(parts.shift())
    const val = parts.length > 0 ? decodeOrKeep(parts.join('=')) : null
    if (res[key] === undefined) {
      res[key] = val
    } else if (Array.isArray(res[key])) {
      res[key].push(val)
    } else {
      res[key] = [res[key], val]
    }
  })
  return res
}

/**
 * 去掉地址（绝对或相对）里这几个键的参数（不管几个、有没有值）；一个都没有 ⇒ 原样还回去（同一个字符串）。
 * @param {String} href
 * @param {String[]} keys
 * @returns {String}
 */
export function withoutQueryKeys(href, keys) {
  if (typeof href !== 'string' || !Array.isArray(keys) || !keys.length) return href
  const hashAt = href.indexOf('#')
  const beforeHash = hashAt < 0 ? href : href.slice(0, hashAt)
  const hash = hashAt < 0 ? '' : href.slice(hashAt)
  const queryAt = beforeHash.indexOf('?')
  if (queryAt < 0) return href
  const parts = beforeHash.slice(queryAt + 1).split('&')
  if (!parts.some(part => keys.includes(queryPartKey(part)))) return href
  const query = parts.filter(part => part !== '' && !keys.includes(queryPartKey(part))).join('&')
  return beforeHash.slice(0, queryAt) + (query ? `?${query}` : '') + hash
}
